import { GuaranteeConfigStruture, GuaranteeVehicleDetailConfig } from "@interfaces/catalogs";
import { GarantiasVehicularInmobiliariaResponse } from "@interfaces/response";
import { GUARANTEE_TYPE } from "@helpers/constantes";
import { GuaranteeFormKind, GarantiaFormValue, VehicleCatalogCodes } from "@interfaces/informacionMultiGarantias";

/**
 * Lógica de dominio de la pantalla "Información de tu garantía" (`InformacionMultiGarantias.tsx`).
 * Todo lo de aquí es puro: sin React, sin Redux y sin llamadas al API, para poder probarse aislado.
 */

export const MAX_GUARANTEES = 10;

/**
 * Detalles de garantía prendaria SIN valuación automática (Libro Azul): el usuario captura marca,
 * modelo, versión y valor estimado a mano. Copiado tal cual del legacy (`vehiculos_manual` en
 * `FormGarantiaVehicularPymio`).
 *
 * TODO: el diseño (`GAR_LIBRO_AZUL` del prototipo) consideraba "Camionetas Pickups" como tipo CON
 * valuación automática; el legacy lo trata como manual. Se respeta el legacy — confirmar cuál es el
 * comportamiento correcto de producto.
 */
export const VEHICULOS_MANUAL = [
  "Vans y Last mile delivery",
  "Tractocamiones",
  "Camionetas",
  "Camionetas Pickups",
  "Maquinaria amarilla",
  "Montacargas",
];

/** Detalles donde el "kilometraje" en realidad son horas de uso (mismo criterio del legacy). */
export const VEHICULOS_HORAS_USO = ["Maquinaria amarilla", "Montacargas"];

/** Equivalente al `guaranteeFormMap` del legacy: qué formulario corresponde a cada tipo de garantía. */
const GUARANTEE_FORM_BY_CODE: Record<string, GuaranteeFormKind> = {
  [GUARANTEE_TYPE.prendaria.code]: "vehicular",
  [GUARANTEE_TYPE.vehicular.code]: "vehicular",
  [GUARANTEE_TYPE.inmobiliaria.code]: "inmobiliaria",
  [GUARANTEE_TYPE.cuentas.code]: null,
};

const createLocalId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const emptyGarantia = (): GarantiaFormValue => ({
  localId: createLocalId(),
  guaranteeId: undefined,
  guarantee_type_id: "",
  type_inmobiliaria: "",
  valor_estimado: "",
  ubicacion: "",
  latitud: "",
  longitud: "",
  type_vehicle: "",
  year: "",
  brand: "",
  brand_value: "",
  model: "",
  model_value: "",
  version: "",
  version_value: "",
  km: "",
  estimated_value: "",
});

const guaranteeCodeOf = (config: GuaranteeConfigStruture[], guaranteeTypeId: string) =>
  config.find((item) => item.guarantee_type_id === guaranteeTypeId)?.guarantee_type?.code ?? "";

export const formKindOf = (config: GuaranteeConfigStruture[], guaranteeTypeId: string): GuaranteeFormKind =>
  GUARANTEE_FORM_BY_CODE[guaranteeCodeOf(config, guaranteeTypeId)] ?? null;

export const detailNameOf = (details: GuaranteeVehicleDetailConfig[] | undefined, detailId: string) =>
  details?.find((item) => item.guarantee_type_detail_id === detailId)?.guarantee_type_detail.name ?? "";

/** Descarta todo lo que no sea dígito: captura de importes, kilometraje y horas de uso. */
export const onlyDigits = (raw: string) => raw.replace(/[^0-9]/g, "");

/** Solo dígitos, con separador de miles; cadena vacía cuando el campo se limpia. */
export const formatAmount = (raw: string) => {
  const digits = onlyDigits(raw);
  return digits ? Number(digits).toLocaleString("en-US") : "";
};

export const cleanAmount = (value: string) => Number(String(value ?? "").replace(/,/g, "")) || 0;

export const EMPTY_VEHICLE_CODES: VehicleCatalogCodes = { brand: "", model: "" };

/** Garantías que la pantalla puede mostrar: solo activas y hasta el máximo permitido. */
export const selectActiveGuarantees = (data: GarantiasVehicularInmobiliariaResponse[] | undefined) =>
  (data ?? []).filter((item) => item.active).slice(0, MAX_GUARANTEES);

/** Id del detalle (tipo de inmueble / vehículo) a partir del nombre que guardó el backend. */
const findDetailIdByName = (details: GuaranteeVehicleDetailConfig[], name: string) =>
  details.find((item) => item.guarantee_type_detail.name === name)?.guarantee_type_detail_id ?? "";

/** Maquinaria amarilla y montacargas capturan horas de uso, sin separador de miles. */
const formatMileage = (vehicleType: string, mileage?: number) =>
  VEHICULOS_HORAS_USO.includes(vehicleType) ? String(mileage ?? "") : mileage?.toLocaleString("en-US") || "";

const formatEstimatedValue = (value?: number | null) => {
  const amount = Number(value ?? 0);
  return amount > 0 ? amount.toLocaleString("en-US") : "";
};

/** Garantía inmobiliaria del backend → valores del formulario. */
export const mapInmobiliariaGuarantee = (
  guarantee: GarantiasVehicularInmobiliariaResponse,
  base: GarantiaFormValue,
): GarantiaFormValue => ({
  ...base,
  type_inmobiliaria: guarantee.guarantee_type_detail_id || "",
  valor_estimado: Number(guarantee.estimated_value).toLocaleString("en-US") || "0",
  latitud: String(guarantee.latitude || ""),
  longitud: String(guarantee.longitude || ""),
  ubicacion: guarantee.address || "",
});

/**
 * Garantía prendaria/vehicular del backend → valores del formulario. Los códigos de marca y modelo
 * del Libro Azul se resuelven aparte (el backend guarda nombres, los selects usan códigos).
 */
export const mapVehicularGuarantee = (
  guarantee: GarantiasVehicularInmobiliariaResponse,
  details: GuaranteeVehicleDetailConfig[],
  codes: VehicleCatalogCodes,
  base: GarantiaFormValue,
): GarantiaFormValue => {
  const vehicleType = String(guarantee.details?.vehicle_type || "");
  const shared: GarantiaFormValue = {
    ...base,
    type_vehicle: findDetailIdByName(details, vehicleType),
    year: guarantee.details?.year?.toString() || "",
    brand_value: guarantee.details?.make || "",
    model_value: guarantee.details?.model || "",
    version_value: guarantee.details?.version || "",
    km: formatMileage(vehicleType, guarantee.details?.estimated_mileage),
    estimated_value: formatEstimatedValue(guarantee.estimated_value),
  };

  // Captura manual: no hay catálogo, el "código" y el valor visible son el mismo texto.
  if (VEHICULOS_MANUAL.includes(vehicleType)) {
    return {
      ...shared,
      brand: guarantee.details?.make || "",
      model: guarantee.details?.model || "",
      version: guarantee.details?.version || "",
    };
  }

  return {
    ...shared,
    brand: codes.brand,
    model: codes.model,
    version: guarantee.details?.car_version_code || "",
  };
};
